import { create } from 'zustand';
import { defaultAdjustments, defaultBeauty, defaultMetadata, defaultToolSettings, type EditorDocument, type PanelId, type ToolId, type ToolSettings } from './types';
import { decodeFile, uid } from './lib/image';

interface EditorState {
  doc: EditorDocument | null;
  activePanel: PanelId;
  activeTool: ToolId;
  selectedLayerId: string | null;
  zoom: number;
  compare: boolean;
  history: EditorDocument[];
  future: EditorDocument[];
  loading: boolean;
  toolSettings: ToolSettings;
  setPanel: (panel: PanelId) => void;
  setTool: (tool: ToolId) => void;
  setZoom: (zoom: number) => void;
  setCompare: (compare: boolean) => void;
  setToolSettings: (patch: Partial<ToolSettings>) => void;
  loadFile: (file: File) => Promise<void>;
  loadDocument: (doc: EditorDocument) => void;
  updateDoc: (recipe: (doc: EditorDocument) => EditorDocument, checkpoint?: boolean) => void;
  checkpoint: () => void;
  undo: () => void;
  redo: () => void;
  selectLayer: (id: string) => void;
  reset: () => void;
}

const clone = (doc: EditorDocument) => structuredClone(doc);

export const useEditorStore = create<EditorState>((set, get) => ({
  doc: null,
  activePanel: 'adjust', activeTool: 'move', selectedLayerId: null,
  zoom: 1, compare: false, history: [], future: [], loading: false,
  toolSettings: { ...defaultToolSettings },
  setPanel: activePanel => set({ activePanel }),
  setTool: activeTool => set({ activeTool }),
  setZoom: zoom => set({ zoom: Math.max(.15, Math.min(4, zoom)) }),
  setCompare: compare => set({ compare }),
  setToolSettings: patch => set(state => ({ toolSettings: { ...state.toolSettings, ...patch } })),
  async loadFile(file) {
    set({ loading: true });
    try {
      const { source, image } = await decodeFile(file);
      const id = uid();
      const doc: EditorDocument = {
        id, name: file.name.replace(/\.[^.]+$/, ''), sourceName: file.name, sourceType: file.type, sourceBytes: file.size,
        width: image.naturalWidth, height: image.naturalHeight,
        createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
        layers: [{ id: uid(), name: '原始照片', type: 'image', source, visible: true, locked: true, opacity: 1, blendMode: 'source-over' }],
        strokes: [], liquifyStrokes: [], adjustments: { ...defaultAdjustments }, beauty: { ...defaultBeauty }, metadata: { ...defaultMetadata },
        rotation: 0, flipX: false, flipY: false, cropAspect: 'free', cropRect: { x: 0, y: 0, width: 1, height: 1 }, faceStatus: 'detecting', faceBoxes: []
      };
      set({ doc, selectedLayerId: doc.layers[0].id, history: [], future: [], zoom: 1, loading: false });
      import('./lib/vision').then(({ detectFaces }) => detectFaces(source)).then(faceBoxes => {
        const current = get().doc;
        if (current?.id === id) set({ doc: { ...current, faceBoxes, faceStatus: 'ready' } });
      }).catch(() => {
        const current = get().doc;
        if (current?.id === id) set({ doc: { ...current, faceStatus: 'unavailable' } });
      });
    } catch (error) { set({ loading: false }); throw error; }
  },
  loadDocument: doc => {
    const normalized = { ...doc, sourceBytes: doc.sourceBytes || 0, liquifyStrokes: doc.liquifyStrokes || [], cropRect: doc.cropRect || { x: 0, y: 0, width: 1, height: 1 }, faceStatus: doc.faceBoxes?.length ? 'ready' as const : 'detecting' as const, faceBoxes: doc.faceBoxes || [] };
    set({ doc: normalized, selectedLayerId: doc.layers.at(-1)?.id || null, history: [], future: [], zoom: 1 });
    const source = normalized.layers.find(layer => layer.source)?.source;
    if (!source || normalized.faceBoxes.length) return;
    import('./lib/vision').then(({ detectFaces }) => detectFaces(source)).then(faceBoxes => {
      const current = get().doc;
      if (current?.id === normalized.id) set({ doc: { ...current, faceBoxes, faceStatus: 'ready' } });
    }).catch(() => {
      const current = get().doc;
      if (current?.id === normalized.id) set({ doc: { ...current, faceStatus: 'unavailable' } });
    });
  },
  updateDoc(recipe, checkpoint = false) {
    const current = get().doc; if (!current) return;
    const next = { ...recipe(current), updatedAt: new Date().toISOString() };
    set(state => ({ doc: next, history: checkpoint ? [...state.history.slice(-29), clone(current)] : state.history, future: checkpoint ? [] : state.future }));
  },
  checkpoint() { const doc = get().doc; if (doc) set(state => ({ history: [...state.history.slice(-29), clone(doc)], future: [] })); },
  undo() {
    const { doc, history, future } = get(); if (!doc || !history.length) return;
    set({ doc: history.at(-1), history: history.slice(0, -1), future: [clone(doc), ...future].slice(0, 30) });
  },
  redo() {
    const { doc, history, future } = get(); if (!doc || !future.length) return;
    set({ doc: future[0], future: future.slice(1), history: [...history, clone(doc)].slice(-30) });
  },
  selectLayer: selectedLayerId => set({ selectedLayerId }),
  reset: () => set({ doc: null, selectedLayerId: null, history: [], future: [] })
}));
